/** @jsx isolateComponent */
import { Fragment } from 'react';
import { css } from '@emotion/core';

import { isolateComponent } from '@zapier/style-encapsulation';
import { DateTime } from '@zapier/date';
import { TooltipWrapper } from '../../display/TooltipWrapper';

import { isSameDay } from './utils';
import { DateOption, DateRange } from './types';

type Props = {
  /** an optional list of predefined DateRanges the user can select from */
  options: Array<DateOption>;
  value: DateRange;
};

const Styles = {
  root: css`
    display: block;
    overflow: hidden;
    white-space: nowrap;
  `,
  wrapper: css`
    display: flex;
    max-width: 100%;
    overflow: hidden;
  `,
};

function getRangeText(value: DateRange) {
  const isSameDate = isSameDay(value.from, value.to);

  const from = value.from ? (
    // We have to ignore profile timezone here and just stick to the browser
    // since there could be a huge mismatch which would result in the days showing
    // different values 😭
    <DateTime
      date={value.from}
      format="MM/DD/YY"
      ignoreProfileTimezone={true}
    />
  ) : null;

  // Do not show 'to' if it is the same date as 'from'
  const to =
    !isSameDate && value.to ? (
      <DateTime
        date={value.to}
        format="MM/DD/YY"
        ignoreProfileTimezone={true}
      />
    ) : null;

  return (
    <Fragment>
      {from}
      {Boolean(from) && Boolean(to) && ' – '}
      {to}
    </Fragment>
  );
}

export const DateRangePickerValue = ({ options, value }: Props) => {
  const selectedOption = options.find(
    (option) =>
      isSameDay(option.value.from, value.from) &&
      isSameDay(option.value.to, value.to)
  );

  const rangeText = getRangeText(value);

  return (
    <div css={Styles.wrapper}>
      <TooltipWrapper content={selectedOption ? rangeText : null}>
        {(childProps) => (
          <span {...childProps} css={Styles.root}>
            {selectedOption ? selectedOption.text : rangeText}
          </span>
        )}
      </TooltipWrapper>
    </div>
  );
};
